import React, { useRef, useState } from 'react';
import { api } from '../../api/client';

import { ta } from '../../i18n/adminT';
export default function BackupManager() {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);

  const [licenseExporting, setLicenseExporting] = useState(false);
  const [licenseExportError, setLicenseExportError] = useState('');
  const [licenseImporting, setLicenseImporting] = useState(false);
  const [licenseImportError, setLicenseImportError] = useState('');
  const [licenseImportNote, setLicenseImportNote] = useState('');
  const licenseFileInputRef = useRef(null);

  const handleExport = async () => {
    setExportError('');
    setExporting(true);
    try {
      await api.exportBackup();
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportError('');
    setImportSummary(null);
    setImporting(true);
    try {
      const text = await file.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new Error(ta('Il file selezionato non è un JSON valido.'));
      }
      const res = await api.importBackup(payload);
      setImportSummary(res.summary);
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLicenseExport = async () => {
    setLicenseExportError('');
    setLicenseExporting(true);
    try {
      await api.exportLicenseBackup();
    } catch (err) {
      setLicenseExportError(err.message);
    } finally {
      setLicenseExporting(false);
    }
  };

  const handleLicenseFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLicenseImportError('');
    setLicenseImportNote('');
    setLicenseImporting(true);
    try {
      const res = await api.importLicenseBackup(file);
      setLicenseImportNote(ta(res.note || 'Licenza ripristinata.'));
    } catch (err) {
      setLicenseImportError(err.message);
    } finally {
      setLicenseImporting(false);
      if (licenseFileInputRef.current) licenseFileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <h1 className="admin-page-title">{ta('Backup')}</h1>
      <p className="admin-page-subtitle">
        {ta('Esporta e importa tutti i dati — impostazioni, registrazioni, utenti esterni, persone di riferimento, dispositivi autorizzati, contatti — con tutti i collegamenti tra loro intatti, per spostarli su un\'altra installazione o ripristinarli dopo un problema.')}
      </p>

      <div className="admin-card" style={{ maxWidth: 520 }}>
        <h3 style={{ marginBottom: 10 }}>{ta('Esporta')}</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--muted-text)', marginBottom: 14 }}>
          {ta('Genera un file JSON con tutti i dati di questa installazione, collegamenti compresi (es. quale persona di riferimento è associata a quale utente esterno, da quale dispositivo arriva ogni registrazione). Non include amministratori, documenti PDF (i file vanno ricaricati a parte sulla nuova installazione) né la password SMTP (per sicurezza, andrà reinserita manualmente dopo l\'import).')}
        </p>
        {exportError && <div className="form-error">{exportError}</div>}
        <button type="button" className="btn btn-primary" onClick={handleExport} disabled={exporting} style={{ width: 'auto', padding: '10px 20px' }}>
          {exporting ? ta('Esportazione…') : ta('Esporta backup (JSON)')}
        </button>
      </div>

      <div className="admin-card" style={{ maxWidth: 520 }}>
        <h3 style={{ marginBottom: 10 }}>{ta('Importa')}</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--muted-text)', marginBottom: 14 }}>
          {ta('Seleziona un file di backup esportato da ExelFlow. I record già presenti (stesso identificativo originale, o che vanno in conflitto su un campo che dev\'essere unico, come il codice o il badge NFC di un utente esterno) vengono riconosciuti e saltati automaticamente: puoi importare lo stesso file più volte senza creare duplicati.')}
        </p>
        {importError && <div className="form-error">{importError}</div>}
        {importSummary && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '12px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Importazione completata.')}<br />
            {ta('Impostazioni importate:')} {importSummary.settingsImported}<br />
            {ta('Persone di riferimento:')} {importSummary.referenceContactsImported} {ta('importate (saltate:')} {importSummary.referenceContactsSkipped})<br />
            {ta('Dispositivi autorizzati:')} {importSummary.authorizedDevicesImported} {ta('importati (saltati:')} {importSummary.authorizedDevicesSkipped})<br />
            {ta('Utenti esterni:')} {importSummary.externalUsersImported} {ta('importati (saltati:')} {importSummary.externalUsersSkipped})<br />
            {ta('Collegamenti utente esterno ↔ riferimento:')} {importSummary.externalUserReferencesImported} {ta('importati')}<br />
            {ta('Contatti:')} {importSummary.eventContactsImported} {ta('importati (saltati:')} {importSummary.eventContactsSkipped})<br />
            {ta('Registrazioni:')} {importSummary.registrationsImported} {ta('importate (saltate:')} {importSummary.registrationsSkipped})
          </div>
        )}
        <div className="upload-drop">
          <label htmlFor="backup-file">{importing ? ta('Importazione…') : ta('Scegli file di backup (.json)')}</label>
          <input
            id="backup-file"
            ref={fileInputRef}
            type="file"
            accept="application/json"
            onChange={handleFileChange}
            disabled={importing}
          />
        </div>
      </div>

      <div className="admin-card" style={{ maxWidth: 520 }}>
        <h3 style={{ marginBottom: 10 }}>{ta('Licenza')}</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--muted-text)', marginBottom: 14 }}>
          {ta('Salva una copia dei file di licenza di questa installazione, utile prima di reinstallare o aggiornare l\'app: la nuova copia dei file parte sempre senza licenza, e altrimenti andrebbe rigenerata da capo con lo strumento')} <span className="mono">{ta('license-tool')}</span>.
        </p>

        {licenseExportError && <div className="form-error">{licenseExportError}</div>}
        <button
          type="button"
          className="btn btn-ghost"
          onClick={handleLicenseExport}
          disabled={licenseExporting}
          style={{ width: 'auto', padding: '10px 18px', marginBottom: 20, border: '1.5px solid var(--border-hair)', borderRadius: 'var(--radius-control)' }}
        >
          {licenseExporting ? ta('Esportazione…') : ta('Esporta backup licenza (ZIP)')}
        </button>

        {licenseImportError && <div className="form-error">{licenseImportError}</div>}
        {licenseImportNote && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {licenseImportNote}
          </div>
        )}
        <div className="upload-drop">
          <label htmlFor="license-backup-file">{licenseImporting ? ta('Ripristino…') : ta('Ripristina da backup (.zip)')}</label>
          <input
            id="license-backup-file"
            ref={licenseFileInputRef}
            type="file"
            accept=".zip,application/zip"
            onChange={handleLicenseFileChange}
            disabled={licenseImporting}
          />
        </div>
        <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginTop: 10 }}>
          {ta('Per sicurezza viene ripristinato solo')} <span className="mono">{ta('license.lic')}</span>{ta(': la chiave pubblica non viene mai sovrascritta da qui. La modifica ha effetto al prossimo riavvio del server.')}
        </p>
      </div>
    </div>
  );
}
